import { Injectable, inject } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Subscription, merge } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UndoSnackBarComponent } from './undo-snack-bar.component';

export interface UndoAction {
    /** Message shown in the toast. */
    message: string;
    /** How long the undo window is open in ms. Defaults to 30 000. */
    durationMs?: number;
    /** Called when the window expires, the user clicks Dismiss, or a new toast supersedes this one. */
    commit: () => void;
    /** Called only when the user clicks Undo — cancel timer + no API call. */
    undo: () => void;
}

/**
 * Root service that owns the deferred-commit lifecycle for undo-able actions.
 *
 * The pending action and the toast reference live here so they survive component
 * destruction (e.g., BrandReviewComponent is destroyed while the toast counts down
 * after navigating back to the queue). The countdown timer lives in UndoSnackBarComponent,
 * which also survives navigation because it is rendered in the CDK overlay at app root.
 *
 * Only one toast is open at a time (#13): a new enqueue commits the prior action.
 */
@Injectable({ providedIn: 'root' })
export class UndoToastService {
    private readonly matSnackBar = inject(MatSnackBar);

    private pendingAction: UndoAction | null = null;
    private subscription: Subscription | null = null;
    private activeRef: MatSnackBarRef<UndoSnackBarComponent> | null = null;

    /**
     * Enqueue a new undoable action.
     *
     * If a prior action is pending its commit is fired first (supersede = commit, #13).
     * Then opens the toast, starts the authoritative timer, and wires Undo / Dismiss.
     */
    enqueue(action: UndoAction): void {
        // Supersede any prior pending action: commit it before opening the new toast.
        this.settle('commit');

        this.pendingAction = action;
        const durationMs = action.durationMs ?? 30000;

        const ref = this.matSnackBar.openFromComponent(UndoSnackBarComponent, {
            data: { message: action.message, durationMs },
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: 'undo-snackbar-panel',
        });
        this.activeRef = ref;

        // First-event-wins: undo cancels, all other outcomes commit.
        // afterDismissed() catches external closes (e.g. another snackbar clobbering ours)
        // so commit() is guaranteed to fire even if no button is clicked.
        // The component's undo/dismiss/expired are output() emitters (OutputEmitterRef),
        // which expose only subscribe() — not RxJS operators — so convert them with
        // outputToObservable() before merging. afterDismissed() is already an Observable.
        this.subscription = merge(
            outputToObservable(ref.instance.undo).pipe(map(() => 'undo' as const)),
            outputToObservable(ref.instance.dismiss).pipe(map(() => 'commit' as const)),
            outputToObservable(ref.instance.expired).pipe(map(() => 'commit' as const)),
            ref.afterDismissed().pipe(map(() => 'commit' as const)),
        ).pipe(take(1)).subscribe(outcome => this.settle(outcome));
    }

    // ── Private ───────────────────────────────────────────────────────────────

    /** Commit or undo the pending action, then tear down all state. */
    private settle(outcome: 'commit' | 'undo'): void {
        if (!this.pendingAction) { return; }
        const action = this.pendingAction;
        this.teardown();
        outcome === 'undo' ? action.undo() : action.commit();
    }

    /**
     * Unsubscribe and null out all ephemeral state. Dismisses the overlay last so that
     * any re-entry via afterDismissed() finds pendingAction already cleared.
     */
    private teardown(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
        const ref = this.activeRef;
        this.activeRef = null;
        this.pendingAction = null;
        ref?.dismiss();
    }
}
